import { __assign } from 'tslib';

/**
 * Source: ftp://ftp.unicode.org/Public/UCD/latest/ucd/SpecialCasing.txt
 */
/**
 * Lower case as a function.
 */
function lowerCase(str) {
    return str.toLowerCase();
}

// Support camel case ("camelCase" -> "camel Case" and "CAMELCase" -> "CAMEL Case").
var DEFAULT_SPLIT_REGEXP = [/([a-z0-9])([A-Z])/g, /([A-Z])([A-Z][a-z])/g];
// Remove all non-word characters.
var DEFAULT_STRIP_REGEXP = /[^A-Z0-9]+/gi;
/**
 * Normalize the string into something other libraries can manipulate easier.
 */
function noCase(input, options) {
    if (options === void 0) { options = {}; }
    var _a = options.splitRegexp, splitRegexp = _a === void 0 ? DEFAULT_SPLIT_REGEXP : _a, _b = options.stripRegexp, stripRegexp = _b === void 0 ? DEFAULT_STRIP_REGEXP : _b, _c = options.transform, transform = _c === void 0 ? lowerCase : _c, _d = options.delimiter, delimiter = _d === void 0 ? " " : _d;
    var result = replace(replace(input, splitRegexp, "$1\0$2"), stripRegexp, "\0");
    var start = 0;
    var end = result.length;
    // Trim the delimiter from around the output string.
    while (result.charAt(start) === "\0")
        start++;
    while (result.charAt(end - 1) === "\0")
        end--;
    // Transform each token independently.
    return result.slice(start, end).split("\0").map(transform).join(delimiter);
}
/**
 * Replace `re` in the input string with the replacement value.
 */
function replace(input, re, value) {
    if (re instanceof RegExp)
        return input.replace(re, value);
    return re.reduce(function (input, re) { return input.replace(re, value); }, input);
}

function pascalCaseTransform(input, index) {
    var firstChar = input.charAt(0);
    var lowerChars = input.substr(1).toLowerCase();
    if (index > 0 && firstChar >= "0" && firstChar <= "9") {
        return "_" + firstChar + lowerChars;
    }
    return "" + firstChar.toUpperCase() + lowerChars;
}
function pascalCase(input, options) {
    if (options === void 0) { options = {}; }
    return noCase(input, __assign({ delimiter: "", transform: pascalCaseTransform }, options));
}

function camelCaseTransform(input, index) {
    if (index === 0)
        return input.toLowerCase();
    return pascalCaseTransform(input, index);
}
function camelCase(input, options) {
    if (options === void 0) { options = {}; }
    return pascalCase(input, __assign({ transform: camelCaseTransform }, options));
}

const bindingRegEx = /{{(.+)}}/;
class CollectionBinding {
    constructor(bindings) {
        this.bindings = bindings;
    }
    bind(data) {
        this.bindings.forEach((binding) => binding.bind(data));
    }
}
class NodeBinding {
    constructor(node, expr) {
        this.node = node;
        this.expr = expr;
    }
}
class TextNodeBinding extends NodeBinding {
    constructor(node, expr) {
        super(node, expr);
    }
    bind(data) {
        const newContent = replaceBindingString(data, this.expr);
        if (newContent !== this.node.textContent) {
            this.node.textContent = newContent;
        }
    }
}
class AttributeBinding extends NodeBinding {
    constructor(node, expr, attr) {
        super(node, expr);
        this.attr = attr;
    }
    bind(data) {
        const newValue = replaceBindingString(data, this.expr);
        const oldValue = this.node.getAttribute(this.attr);
        if (oldValue !== newValue) {
            this.node.setAttribute(this.attr, newValue);
        }
    }
}
class PropertyBinding extends NodeBinding {
    constructor(node, expr, prop) {
        super(node, expr);
        this.prop = prop;
    }
    bind(data) {
        const newValue = replaceBinding(data, this.expr);
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const oldValue = this.node[this.prop];
        if (oldValue !== newValue) {
            this.node[this.prop] = newValue;
        }
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }
}
class EventHandlerBinding extends NodeBinding {
    constructor(node, expr, eventName) {
        super(node, expr);
        this.eventName = eventName;
    }
    bind(data) {
        var _a;
        const path = extractBindingPath(this.expr);
        if (path != null) {
            (_a = this.disposable) === null || _a === void 0 ? void 0 : _a.dispose();
            const listener = getBindableValue(data, path, true);
            this.node.addEventListener(this.eventName, listener);
            this.disposable = {
                dispose: () => {
                    this.node.removeEventListener(this.eventName, listener);
                },
            };
        }
    }
}
function generateBindings(node) {
    const bindings = [];
    if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node;
        const bindableAttributes = getBindableAttributes(el);
        bindableAttributes.forEach((attr) => {
            if (attr.name.startsWith('event:')) {
                const eventName = camelCase(attr.name.replace('event:', ''));
                bindings.push(new EventHandlerBinding(el, attr.value, eventName));
            }
            else if (attr.name.startsWith('attr:')) {
                bindings.push(new AttributeBinding(el, attr.value, attr.name.replace('attr:', '')));
            }
            else if (attr.name.startsWith('prop:')) {
                const propName = camelCase(attr.name.replace('prop:', ''));
                bindings.push(new PropertyBinding(el, attr.value, propName));
            }
        });
    }
    else if (node.nodeType === Node.TEXT_NODE &&
        node.textContent != null &&
        bindingRegEx.test(node.textContent)) {
        bindings.push(new TextNodeBinding(node, node.textContent));
    }
    for (let i = 0; i < node.childNodes.length; i++) {
        bindings.push(...generateBindings(node.childNodes[i]));
    }
    return bindings;
}
function getBindableAttributes(element) {
    return Array.from(element.attributes).filter((attr) => bindingRegEx.test(attr.value));
}
function extractBindingPath(expr) {
    const result = bindingRegEx.exec(expr);
    return result != null ? result[1] : undefined;
}
function replaceBindingString(data, expr) {
    const path = extractBindingPath(expr);
    if (path != null) {
        const value = getBindableValue(data, path, true);
        return expr.replace(`{{${path}}}`, value === null || value === void 0 ? void 0 : value.toString());
    }
    else {
        return expr;
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function replaceBinding(data, expr) {
    const path = extractBindingPath(expr);
    if (path != null) {
        const value = getBindableValue(data, path, true);
        return value;
    }
    else {
        return expr;
    }
}
function getBindableValue(data, path, isHead = false
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) {
    const [head, ...tail] = path.split('.');
    if (isHead && tail.length === 0) {
        return data;
    }
    else if (isHead && tail.length > 0) {
        return getBindableValue(data, tail.join('.'), false);
    }
    else {
        const value = data[head];
        if (tail.length > 0) {
            return getBindableValue(value, tail.join('.'), false);
        }
        else {
            return value;
        }
    }
}

class ElementPool {
    constructor(container, elementFactory) {
        this.container = container;
        this.elementFactory = elementFactory;
        this.instanceMap = new Map();
        this.elements = [];
    }
    swapHeadToTail(count) {
        const sliced = this.elements.splice(0, count);
        this.elements.splice(this.elements.length, 0, ...sliced);
        return this.elements.concat();
    }
    swapTailToHead(count) {
        const sliced = this.elements.splice(-count, count);
        this.elements.splice(0, 0, ...sliced);
        return this.elements.concat();
    }
    updateElements(count) {
        const diff = count - this.elements.length;
        if (diff > 0) {
            for (let i = 0; i < diff; i++) {
                this.createElement();
            }
        }
        else {
            for (let i = 0; i < -diff; i++) {
                this.deleteElement();
            }
        }
        return this.elements.concat();
    }
    updateData(f) {
        this.elements.forEach((el, i) => {
            const instance = this.instanceMap.get(el);
            const data = f(i);
            instance === null || instance === void 0 ? void 0 : instance.bindings.bind(data);
        });
    }
    updateElementFactory(elementFactory) {
        this.elementFactory = elementFactory;
        this.updateElements(0);
    }
    iterateElements(f) {
        this.elements.forEach((el, i) => {
            const instance = this.instanceMap.get(el);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            f(el, instance.bindings, i);
        });
    }
    createElement() {
        const instance = this.elementFactory();
        this.elements.push(instance.element);
        this.instanceMap.set(instance.element, instance);
        this.container.append(instance.element);
        return instance;
    }
    deleteElement() {
        const element = this.elements.pop();
        if (element != null) {
            this.instanceMap.delete(element);
            element.remove();
        }
    }
}

function append(container, element, data) {
    const bindings = new CollectionBinding(generateBindings(element));
    bindings.bind(data);
    container.appendChild(element);
    const created = container.lastElementChild;
    if (created != null) {
        return { element: created, bindings };
    }
    else {
        throw new Error('Failed to append element');
    }
}
function generateInstanceFromTemplate(template) {
    const fragment = template.content.cloneNode(true);
    const element = fragment.firstElementChild;
    const bindings = new CollectionBinding(generateBindings(fragment));
    return { element, bindings };
}

export { AttributeBinding, CollectionBinding, ElementPool, EventHandlerBinding, NodeBinding, PropertyBinding, TextNodeBinding, append, generateBindings, generateInstanceFromTemplate };
//# sourceMappingURL=bundle.js.map
